import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
    Box,
    Button,
    Card,
    CardBody,
    CardHeader,
    Heading,
    Spinner,
    Text,
    useToast,
    Icon,
    Divider,
} from "@chakra-ui/react";
import { CheckCircleIcon, ArrowBackIcon } from "@chakra-ui/icons";
import { supabase } from "../utils/supabaseClient";
import { COLORS } from "../styles/colors";
import {
    friendlyRpcError,
    toStringValues,
    buildCorporateParticipantPayload,
    normalizeCorporatePortalUen,
    parseEmployerContactDefaults,
    mergeEmployerDefaultsIntoForm,
} from "./corporateEnrollHelpers";
import AdditionalFields from "../AdditionalFields";
import { loadCorporateEnrollRunCourseContext } from "./loadCorporateEnrollRunCourseContext";
import { CorporateEnrollRunSummaryCard } from "./CorporateEnrollRunSummaryCard";

const emptyFormBase = {
    participant_name: "",
    participant_email: "",
    registration_type: "",
    course_run_id: "",
    course_type_code: "",
    sponsor_uen: "",
    dob: "",
    displayDob: "",
    nationality: "",
    nric: "",
    mobile_no: "",
    birth_age: "",
    date_signed: "",
};

/** HR-sent link: correct your corporate draft using portal token + draft row id. */
export function CorporateEnrollDraftEditPage() {
    const { token, draftId } = useParams();
    const toast = useToast();

    const [state, setState] = useState("loading");
    const [errorMessage, setErrorMessage] = useState(null);
    const [portalTitle, setPortalTitle] = useState("");
    const [clientCompanyName, setClientCompanyName] = useState(null);
    const [clientCompanyUen, setClientCompanyUen] = useState(null);
    const [runKind, setRunKind] = useState("");
    const [runCode, setRunCode] = useState("");
    const [runLabel, setRunLabel] = useState("");
    const [courseSummary, setCourseSummary] = useState(null);
    const [sessionsFlat, setSessionsFlat] = useState([]);
    const [moduleSessions, setModuleSessions] = useState([]);

    const [templateFieldIds, setTemplateFieldIds] = useState(null);
    const [templateFieldRecords, setTemplateFieldRecords] = useState([]);

    const [formData, setFormData] = useState(emptyFormBase);
    const [validationErrors, setValidationErrors] = useState({});
    const [selectedFiles, setSelectedFiles] = useState({});
    const [uploadingFiles, setUploadingFiles] = useState(new Set());
    const [registrationId, setRegistrationId] = useState(null);

    const [saved, setSaved] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [validateTick, setValidateTick] = useState(0);
    const [additionalFieldsReady, setAdditionalFieldsReady] = useState(false);
    const pendingSubmitRef = useRef(false);

    const listHref = token ? `/corporate-enroll/${token}` : "/";

    useEffect(() => {
        setAdditionalFieldsReady(false);
    }, [token, draftId]);

    const loadPage = useCallback(async () => {
        if (!token || !draftId) {
            setState("missing");
            return;
        }

        const { data: draftRow, error: draftErr } = await supabase.rpc("get_corporate_portal_participant_draft", {
            p_token: token,
            p_draft_id: draftId,
        });
        if (draftErr) throw draftErr;
        if (!draftRow || typeof draftRow !== "object") {
            setState("missing");
            return;
        }

        const d = draftRow;
        const rk = typeof d.run_kind === "string" ? d.run_kind : "";
        const rc = typeof d.run_code === "string" ? d.run_code : "";
        if (!rk || !rc) {
            setState("missing");
            return;
        }

        setPortalTitle(typeof d.portal_title === "string" ? d.portal_title : "");
        setClientCompanyName(typeof d.company_name === "string" ? d.company_name : null);
        const portalUen = normalizeCorporatePortalUen(d.company_uen);
        setClientCompanyUen(portalUen || null);
        setRunKind(rk);
        setRunCode(rc);
        setRunLabel(typeof d.run_label === "string" ? d.run_label : rc);

        const [ctx, runPartsRes] = await Promise.all([
            loadCorporateEnrollRunCourseContext(rk, rc, d.run_label || rc),
            supabase.rpc("get_corporate_portal_run_participants", {
                p_token: token,
                p_run_kind: rk,
                p_run_code: rc,
            }),
        ]);
        if (ctx.missing) {
            setState("missing");
            return;
        }
        setRunLabel(ctx.runLabel);
        setCourseSummary(ctx.courseSummary);
        setSessionsFlat(ctx.sessionsFlat);
        setModuleSessions(ctx.moduleSessions);
        setTemplateFieldIds(ctx.templateFieldIds);
        setTemplateFieldRecords(ctx.templateFieldRecords);

        const pd = d.participant_data && typeof d.participant_data === "object" ? d.participant_data : {};
        const fromDraft = { ...emptyFormBase, ...toStringValues(pd) };
        const employerDefaults = parseEmployerContactDefaults(
            runPartsRes.data && typeof runPartsRes.data === "object" && !Array.isArray(runPartsRes.data)
                ? runPartsRes.data.employer_contact_defaults
                : null,
        );
        let merged = mergeEmployerDefaultsIntoForm(fromDraft, employerDefaults, { onlyIfEmpty: true });
        if (!String(merged.sponsor_uen || "").trim() && portalUen) {
            merged = { ...merged, sponsor_uen: portalUen };
        }
        if (!String(merged.registration_type || "").trim()) {
            merged = { ...merged, registration_type: "Company-Sponsored" };
        }
        setFormData(merged);

        setState("ready");
    }, [token, draftId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setState("loading");
            try {
                await loadPage();
            } catch (e) {
                if (!cancelled) {
                    setErrorMessage(friendlyRpcError(e, "Could not load this page"));
                    setState("error");
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [loadPage]);

    const submitUpdate = useCallback(async () => {
        if (!token || !draftId) return;
        setSubmitting(true);
        try {
            const payload = buildCorporateParticipantPayload(templateFieldRecords, formData);
            const { data: ok, error } = await supabase.rpc("corporate_portal_update_participant_draft", {
                p_token: token,
                p_draft_id: draftId,
                p_participant_data: payload,
            });
            if (error) throw error;
            if (!ok) throw new Error("Update failed");
            setSaved(true);
            toast({
                title: "Updates saved",
                description: "Your training provider has your latest details.",
                status: "success",
                duration: 6000,
                isClosable: true,
            });
        } catch (err) {
            toast({
                title: "Could not save",
                description: friendlyRpcError(err, "Try again or contact your HR contact."),
                status: "error",
                duration: 6000,
                isClosable: true,
            });
        } finally {
            setSubmitting(false);
        }
    }, [token, draftId, templateFieldRecords, formData, toast]);

    const onValidateComplete = useCallback(
        (ok) => {
            if (!pendingSubmitRef.current) return;
            pendingSubmitRef.current = false;
            if (!ok) {
                toast({
                    title: "Please complete required fields",
                    description: "Check highlighted sections below.",
                    status: "warning",
                    duration: 5000,
                    isClosable: true,
                });
                return;
            }
            void submitUpdate();
        },
        [submitUpdate, toast],
    );

    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (!additionalFieldsReady) {
            toast({
                title: "Still loading the form",
                description: "Please wait a moment and try again.",
                status: "info",
                duration: 4000,
                isClosable: true,
            });
            return;
        }
        pendingSubmitRef.current = true;
        setValidateTick((t) => t + 1);
    };

    const shell = (inner) => (
        <Box minH="100vh" bgGradient="linear(to-b, #F4EEEA, white)" py={{ base: 8, md: 12 }} px={4}>
            <Box maxW="container.md" mx="auto">
                {inner}
            </Box>
        </Box>
    );

    if (state === "loading") {
        return shell(
            <Card>
                <CardBody py={16} textAlign="center">
                    <Spinner size="lg" color={COLORS.mandarin} />
                    <Text mt={4} fontSize="sm" color="gray.600">
                        Loading…
                    </Text>
                </CardBody>
            </Card>,
        );
    }

    if (state === "error") {
        return shell(
            <Card>
                <CardBody py={10}>
                    <Text color="red.600" textAlign="center">
                        {errorMessage}
                    </Text>
                    <Button as={RouterLink} to={listHref} mt={6} leftIcon={<ArrowBackIcon />}>
                        Back
                    </Button>
                </CardBody>
            </Card>,
        );
    }

    if (state === "missing") {
        return shell(
            <Card>
                <CardBody py={10} textAlign="center">
                    <Text color="gray.600">
                        This link is invalid or expired. Ask your HR contact for a new link.
                    </Text>
                </CardBody>
            </Card>,
        );
    }

    if (saved) {
        return shell(
            <Card shadow="lg" borderWidth="1px">
                <CardBody py={12} textAlign="center">
                    <Icon as={CheckCircleIcon} w={14} h={14} color={COLORS.shinyShamrock} mb={4} />
                    <Heading size="md" color={COLORS.maastrichtBlue} mb={2}>
                        Saved
                    </Heading>
                    <Text color="gray.600" fontSize="sm" maxW="lg" mx="auto" mb={6}>
                        You can close this page. If HR sends another request, use the link from their email again.
                    </Text>
                </CardBody>
            </Card>,
        );
    }

    return (
        <Box minH="100vh" bgGradient="linear(to-b, #F4EEEA, white)" py={{ base: 8, md: 12 }} px={4}>
            <Box maxW="container.md" mx="auto">
                <Text fontSize="sm" color="gray.600" mb={4}>
                    You received this link to review or correct your registration details.
                </Text>

                <CorporateEnrollRunSummaryCard
                    portalTitle={portalTitle}
                    clientCompanyName={clientCompanyName}
                    clientCompanyUen={clientCompanyUen}
                    courseSummary={courseSummary}
                    runLabel={runLabel}
                    runCode={runCode}
                    moduleSessions={moduleSessions}
                    sessionsFlat={sessionsFlat}
                />

                <Card shadow="md" borderWidth="1px">
                    <CardHeader>
                        <Heading size="sm" color={COLORS.maastrichtBlue}>
                            Your registration
                        </Heading>
                        <Text fontSize="sm" color="gray.600" mt={1}>
                            Update any fields HR or your provider flagged, then save.
                        </Text>
                    </CardHeader>
                    <CardBody>
                        <Box as="form" onSubmit={handleFormSubmit}>
                            <AdditionalFields
                                key={`${token}-${draftId}`}
                                formData={formData}
                                setFormData={setFormData}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                                registrationId={registrationId}
                                setRegistrationId={setRegistrationId}
                                templateFieldIds={templateFieldIds}
                                selectedFiles={selectedFiles}
                                setSelectedFiles={setSelectedFiles}
                                uploadingFiles={uploadingFiles}
                                setUploadingFiles={setUploadingFiles}
                                corporateEnrollMode
                                corporateValidateTick={validateTick}
                                corporateOnValidateComplete={onValidateComplete}
                                onFieldsLoaded={() => setAdditionalFieldsReady(true)}
                            />
                            <Divider my={8} />
                            <Button
                                type="submit"
                                w="100%"
                                bg={COLORS.mandarin}
                                color="white"
                                _hover={{ bg: "#d96a3d" }}
                                size="lg"
                                isLoading={submitting}
                                loadingText="Saving"
                                isDisabled={!additionalFieldsReady}
                            >
                                Save changes
                            </Button>
                        </Box>
                    </CardBody>
                </Card>
            </Box>
        </Box>
    );
}
